import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import ShoppingCart from "../ShoppingCart/ShoppingCart";
import { useLocalStorage } from "../hooks/useLocalStorage";
import React from "react";
import { useAppSelector } from "../../../store/hooks";
import { pf } from "../utilities/helpers";
import {
  enrichOrderProductsForConfirmedDisplay,
  mergeInvalidOrderProductsIntoCartItems,
  type OrderProductForDisplay,
} from "../utilities/cartDisplay";
import {
  getConfirmedOrderGrandTotal,
  sumCartDiscount,
  sumCartProductsTotal,
} from "../utilities/cartTotals";
import {
  mergeResolvedPricing,
  type LocationProductAdjustment,
  type ResolvedLocationProductPricing,
} from "../utilities/locationProductAdjustments";
import {
  cartLineRetainedByOrderProducts,
  findMatchingOrderProductForCartLine,
  resyncAutoExtraCartLines,
} from "../utilities/webstoreCartExtras";
import { Product } from "../interfaces/catalogProduct";
import {
  cartItemMatchesManualMutationTarget,
  cartItemMatchesMutationTarget,
  isAutoExtraCartLine,
  type CartLineMutationTarget,
} from "../utilities/cartIdentity";
import { eventCartMutationTarget } from "../utilities/eventCart";
import {
  buildCatalogIndex,
  getCatalogEntry,
  getEffectivePrice,
  type CatalogEntry,
  type CatalogIndex,
} from "../utilities/productCatalog";
import {
  formatDataLayerCategories,
  formatDataLayerProductName,
} from "../utilities/dataLayerCategories";
import { selectListableCatalogProducts } from "../utilities/catalogProductAdapter";
import {
  clampQuantityToMinAndCap,
  clampQuantityToMinOrder,
  isBelowEffectiveMinQty,
  resolveEffectiveMinQty,
  resolveMinOrderQty,
  resolveProductQuantityCap,
  resolveStartQuantity,
} from "../utilities/quantityCaps";
import {
  buildOrderPayRequest,
  buildSyncOrderRequest,
  extractOrderApiMessage,
  formatOrderApiErrors,
  hasOrderApiErrors,
  hasOrderNotEditableError,
  mapBuyerToCustomer,
  mapDeliveryInputToPayload,
  mapCartItemsToOrderProducts as mapOrderProductsForRequest,
  normalizeOrderApiResponse,
  type BuyerFormData,
  type DeliveryFormData,
} from "../utilities/orderApi";
import type {
  CheckoutContext,
  CheckoutDeliveryMethod,
  CheckoutError,
  CheckoutOrder,
  CheckoutOrderDelivery,
  CheckoutPaymentMethod,
  SyncOrderResult,
} from "../utilities/checkoutTypes";
import { isMethodUuidInList } from "../Checkout/checkoutConfig";

declare var API: any;

type BuilderCatalogProductsPrefetchWindow = Window & {
  __catalogProductsPrefetch?: Promise<Product[]>;
};

function normalizeOrderParentProductUuids(orderProduct: {
  parentUuid?: string | null;
  parentProductUuids?: string[];
}): string[] | undefined {
  const normalized = [
    ...new Set(
      [...(orderProduct.parentProductUuids ?? []), orderProduct.parentUuid ?? undefined]
        .filter((uuid): uuid is string => typeof uuid === "string" && uuid.trim().length > 0)
    ),
  ];
  return normalized.length > 0 ? normalized : undefined;
}

function redirectToHostedPayment(url: string): void {
  const isInsideIframe = window.self !== window.top;
  if (!isInsideIframe) {
    window.location.href = url;
    return;
  }

  try {
    window.parent?.postMessage(
      {
        type: "START_PAYMENT",
        url,
      },
      "*"
    );
  } catch (error) {
    console.warn("Unable to notify parent window about payment redirect.", error);
  }

  try {
    if (window.top) {
      window.top.location.href = url;
      return;
    }
  } catch (error) {
    console.warn("Unable to redirect top window to payment URL.", error);
  }

  const popup = window.open(url, "_blank", "noopener,noreferrer");
  if (!popup) {
    window.location.href = url;
  }
}

function cartItemsEqualForSync(a: CartItem[], b: CartItem[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  return a.every((item, index) => {
    const other = b[index];
    if (!other) {
      return false;
    }
    return (
      item.productUuid === other.productUuid &&
      item.quantity === other.quantity &&
      item.maxAvailable === other.maxAvailable &&
      item.maxOrderQty === other.maxOrderQty &&
      item.minOrderQty === other.minOrderQty &&
      item.hasActiveSellOrders === other.hasActiveSellOrders &&
      item.eventHash === other.eventHash &&
      item.start === other.start &&
      item.cartLineSource === other.cartLineSource &&
      item.invalid === other.invalid &&
      item.productName === other.productName &&
      JSON.stringify(item.parentProductUuids ?? []) ===
        JSON.stringify(other.parentProductUuids ?? [])
    );
  });
}

function productsEqualForRefresh(a: Product[], b: Product[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch (_error) {
    return false;
  }
}

const CONFIRMED_PAYMENT_METHOD_STORAGE_KEY =
  "checkout-confirmed-payment-method";

function readConfirmedPaymentMethodFromStorage(): PaymentMethod | null {
  try {
    const raw = sessionStorage.getItem(CONFIRMED_PAYMENT_METHOD_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as PaymentMethod;
    return parsed?.uuid ? parsed : null;
  } catch {
    return null;
  }
}

function writeConfirmedPaymentMethodToStorage(
  method: PaymentMethod | null | undefined
): void {
  try {
    if (method?.uuid) {
      sessionStorage.setItem(
        CONFIRMED_PAYMENT_METHOD_STORAGE_KEY,
        JSON.stringify(method)
      );
    } else {
      sessionStorage.removeItem(CONFIRMED_PAYMENT_METHOD_STORAGE_KEY);
    }
  } catch {
    // ignore storage errors
  }
}

function clearConfirmedPaymentMethodFromStorage(): void {
  try {
    sessionStorage.removeItem(CONFIRMED_PAYMENT_METHOD_STORAGE_KEY);
  } catch {
    // ignore storage errors
  }
}

type ShoppingCartProviderProps = {
  children: ReactNode;
};

type CartItem = {
  /** Catalog/event lines may set productUuid for identity-safe cart operations. */
  productUuid: string;
  quantity: number;
  maxAvailable?: number;
  maxOrderQty?: number;
  minQty?: number;
  minOrderQty?: number;
  eventHash?: string;
  start?: string;
  /** Default extras generated from a parent line; read-only in cart UI. */
  cartLineSource?: "autoExtra" | "eventAdditional";
  /** Parent catalog lines that drive this auto extra (retention when API omits extras). */
  parentProductUuids?: string[];
  /** Synced from order API when productUuid is not sellable on this outlet. */
  invalid?: boolean;
  /** Display name from order API when catalog entry is missing. */
  productName?: string;
  /** Last known global sell-order gate from event API; survives cart persistence. */
  hasActiveSellOrders?: boolean;
};

function getCartLineQuantityCap(item: CartItem): number | undefined {
  return resolveProductQuantityCap(item.maxAvailable, item.maxOrderQty);
}

function resolveEventCartLineQuantityCap(
  cartItems: CartItem[],
  orderProducts: Array<{
    productUuid?: string;
    eventHash?: string;
    maxAvailable?: number;
    maxOrderQty?: number;
  }> | undefined,
  productUuid: string,
  eventRef: string
): number | undefined {
  const target = eventCartMutationTarget({ productUuid, eventRef });
  const fromCart = cartItems.find((item) =>
    cartItemMatchesManualMutationTarget(item, target)
  );

  let maxAvailable =
    fromCart && typeof fromCart.maxAvailable === "number"
      ? fromCart.maxAvailable
      : undefined;
  let maxOrderQty =
    fromCart &&
    typeof fromCart.maxOrderQty === "number" &&
    fromCart.maxOrderQty > 0
      ? fromCart.maxOrderQty
      : undefined;

  const orderProduct = orderProducts?.find((op) => {
    if (productUuid && op.productUuid !== productUuid) return false;
    return op.eventHash === eventRef;
  });

  if (
    maxAvailable === undefined &&
    typeof orderProduct?.maxAvailable === "number"
  ) {
    maxAvailable = orderProduct.maxAvailable;
  }
  if (
    maxOrderQty === undefined &&
    typeof orderProduct?.maxOrderQty === "number" &&
    orderProduct.maxOrderQty > 0
  ) {
    maxOrderQty = orderProduct.maxOrderQty;
  }

  return getCartLineQuantityCap({
    productUuid,
    quantity: fromCart?.quantity ?? 0,
    maxAvailable,
    maxOrderQty,
    eventHash: eventRef,
  });
}

function parseMinQtyForCart(
  minQty: number | null | undefined
): number | undefined {
  return typeof minQty === "number" && Number.isFinite(minQty) && minQty > 0
    ? minQty
    : undefined;
}

function parseMinOrderQtyForCart(
  minOrderQty: number | null | undefined
): number | undefined {
  return typeof minOrderQty === "number" &&
    Number.isFinite(minOrderQty) &&
    minOrderQty > 1
    ? minOrderQty
    : undefined;
}

function applyEventSellOrderHintsToCart(
  cartItems: CartItem[],
  events: Array<{ hash?: string | null; hasActiveSellOrders?: boolean }>
): CartItem[] {
  const byHash = new Map<string, boolean>();
  for (const event of events) {
    const hash = typeof event.hash === "string" ? event.hash.trim() : "";
    if (!hash || event.hasActiveSellOrders === undefined) {
      continue;
    }
    byHash.set(hash, event.hasActiveSellOrders === true);
  }

  if (byHash.size === 0) {
    return cartItems;
  }

  return cartItems.map((item) => {
    const hash = (item.eventHash ?? "").trim();
    if (!hash || !byHash.has(hash)) {
      return item;
    }
    return { ...item, hasActiveSellOrders: byHash.get(hash) };
  });
}

/** True only when the first-order minQty gate is known to be open. Unknown => false. */
function resolveFirstOrderMinQtyEnforced(
  eventHash: string,
  cartItems: CartItem[],
  eventHasActiveSellOrders: Record<string, boolean>
): boolean {
  const normalized = eventHash.trim();
  if (!normalized) {
    return false;
  }

  if (eventHasActiveSellOrders[normalized] === true) {
    return false;
  }
  if (eventHasActiveSellOrders[normalized] === false) {
    return true;
  }

  const linesForEvent = cartItems.filter(
    (item) => (item.eventHash ?? "").trim() === normalized
  );
  if (linesForEvent.some((item) => item.hasActiveSellOrders === true)) {
    return false;
  }
  if (linesForEvent.some((item) => item.hasActiveSellOrders === false)) {
    return true;
  }

  return false;
}

function resolveEventCartLineMinOrderQty(
  cartItems: CartItem[],
  orderProducts: Array<{
    productUuid?: string;
    eventHash?: string;
    minQty?: number;
    minOrderQty?: number;
  }> | undefined,
  productUuid: string,
  eventRef: string,
  eventHasActiveSellOrders: Record<string, boolean>
): number | undefined {
  const target = eventCartMutationTarget({ productUuid, eventRef });
  const fromCart = cartItems.find((item) =>
    cartItemMatchesManualMutationTarget(item, target)
  );
  const enforceFirstOrderMin = resolveFirstOrderMinQtyEnforced(
    eventRef,
    cartItems,
    eventHasActiveSellOrders
  );

  if (fromCart) {
    const effectiveMin = resolveEffectiveMinQty(
      fromCart.minQty,
      fromCart.minOrderQty,
      enforceFirstOrderMin
    );
    if (effectiveMin > 1) {
      return effectiveMin;
    }
  }

  const orderProduct = orderProducts?.find((op) => {
    if (productUuid && op.productUuid !== productUuid) return false;
    return op.eventHash === eventRef;
  });

  if (orderProduct) {
    const effectiveMin = resolveEffectiveMinQty(
      orderProduct.minQty,
      orderProduct.minOrderQty,
      enforceFirstOrderMin
    );
    if (effectiveMin > 1) {
      return effectiveMin;
    }
  }

  return undefined;
}

function clampEventCartLineQuantity(
  quantity: number,
  cartItems: CartItem[],
  orderProducts: Array<{
    productUuid?: string;
    eventHash?: string;
    maxAvailable?: number;
    maxOrderQty?: number;
    minQty?: number;
    minOrderQty?: number;
  }> | undefined,
  productUuid: string,
  eventRef: string,
  eventHasActiveSellOrders: Record<string, boolean>,
  item?: CartItem
): number {
  if (quantity <= 0) {
    return 0;
  }

  let qty = quantity;
  const cap = resolveEventCartLineQuantityCap(
    cartItems,
    orderProducts,
    productUuid,
    eventRef
  );
  const minOrderQty =
    resolveEventCartLineMinOrderQty(
      cartItems,
      orderProducts,
      productUuid,
      eventRef,
      eventHasActiveSellOrders
    ) ??
    (item
      ? resolveEffectiveMinQty(
          item.minQty,
          item.minOrderQty,
          resolveFirstOrderMinQtyEnforced(
            eventRef,
            cartItems,
            eventHasActiveSellOrders
          )
        )
      : undefined);

  return clampQuantityToMinAndCap(qty, minOrderQty, cap);
}


interface CategoryTranslation {
  name: string;
}

interface CategoryTranslations {
  en?: CategoryTranslation;
  ru?: CategoryTranslation;
  et?: CategoryTranslation;

  // Allows additional languages dynamically
  [languageCode: string]: CategoryTranslation | undefined;
}

export interface ProductCategory {
  uuid: string;
  sortOrder?: number;
  parentUuid?: string | null;
  translations: CategoryTranslations;
  children?: ProductCategory[];
  /** Legacy alias still used by some builder menus */
  cashregisterChildren?: ProductCategory[];
}

export interface LocationProductDiscount {
  id: number;
  name: string;
  display_name: string;
  value: string;
  accumulates: number;
  starts_at: string;
  ends_at: string;
  indefinite: number;
}

export interface LocationProduct {
  translations: any;
  id: number;
  shortDescription: string;
  posProductCode: string;
  discounts: any;
  adjustments?: LocationProductAdjustment[] | null;
  price_net_with_discounts: string;
  product: any;
  deliveryTime: any;
  deliveryTimeWithOrdering: any;
  firstFileUrl: string;
  display_name: string;
  name: string;
  price_total: number;
  price_total_net: number;
  price_regular: number;
  price_recular_net: number;
  adjustment_amount: number;
  adjustment_amount_net: number;
  unit_shortname: string;
  productCategories: any[]; // TODO: fix later when we get rid of loadLocationProducts.json
  inStock: boolean;
}

type Order = CheckoutOrder;

type Person = {
  type?: string;
  id: number;
  firstname: string;
  surname: string;
  phone: string;
  email: string;
  language: string;
  country_code: string;
  organization_country_code: string;
  organization_name?: string;
  organization_reg_nr?: string;
  organization_KMKR_number?: string;
  organization_phone?: string;
  organization_email?: string;
  organization_address?: string;
  organization_historical_names?: string;
  organization_ariregister_status?: string;
  organization_ariregister_url?: string;
  representativeFirstname?: string;
  representativeSurname?: string;
};

export type PaymentMethod = CheckoutPaymentMethod;
export type DeliveryMethod = CheckoutDeliveryMethod;

type ShoppingCartContext = {
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
  getItemQuantity: (productUuid: string) => number;
  increaseCartQuantity: (
    productUuid: string,
    eventRef?: string,
    start?: string,
    skipOpenCart?: boolean,
    salesPeriodEndBeforeEventDatetime?: string | number | null,
    maxQty?: number,
    maxOrderQty?: number,
    minOrderQty?: number,
    minQty?: number,
    hasActiveSellOrders?: boolean,
    cartLineSource?: CartItem["cartLineSource"],
  ) => void;
  decreaseCartQuantity: (
    productUuid: string,
    eventRef?: string,
  ) => void;
  setCartQuantity: (
    quantity: number,
    productUuid: string,
    eventRef?: string,
    start?: string,
  ) => void;
  removeFromCart: (
    productUuid: string,
    eventRef?: string,
  ) => void;
  syncOrder: (options?: {
    buyerData?: Person;
    delivery?: DeliveryFormData | { clear: true } | null;
    checkoutContext?: CheckoutContext;
    includeB2bContactPerson?: boolean;
  }) => Promise<SyncOrderResult>;
  setCartExpire: (value: Date) => void;
  cartQuantity: number;
  setCartExpireDate: () => void;
  totalCartSum: number;
  showCartExpire: boolean;
  showCheckoutErrorModal: boolean;
  setShowCheckoutErrorModal: (value: React.SetStateAction<boolean>) => void;
  handleCheckoutErrorModalClose: () => void;
  additionalInfo: string;
  setAdditionalInfo: (value: string) => void;
  setShowCartExpire: (value: React.SetStateAction<boolean>) => void;
  totalCartDiscount: number;
  setTotalCartDiscount: (value: number) => void;
  totalCartSumConfirmed: number;
  productsLoaded: boolean;
  /** True after /webstoreApi/products.json fetch settles (success or error). */
  catalogProductsLoaded: boolean;
  /** Parent catalog rows from products.json. */
  listableCatalogProducts: Product[];
  catalogIndex: CatalogIndex;
  getCatalogEntryByUuid: (productUuid: string) => CatalogEntry | undefined;
  getCatalogProductByUuid: (productUuid: string) => Product | undefined;
  totalCartDiscountConfirmed: number;
  deliveryConfirmed: CheckoutOrderDelivery | null;
  cartItems: CartItem[];
  order: Order;
  buyer: Person;
  STORAGE_EXP_LENGTH: number;
  setBuyer: (value: React.SetStateAction<Person>) => void;
  paymentMethods: PaymentMethod[];
  paymentMethod: PaymentMethod | null;
  setPaymentMethod: (
    value: React.SetStateAction<PaymentMethod | null>
  ) => void;
  deliveryMethods: DeliveryMethod[];
  products: Product[];
  setProducts: (value: React.SetStateAction<Product[]>) => void;

  pricingNow: Date;
  orderPay: (onSuccess?: () => void) => void;
  checkoutErrorMessage?: string;
  clearData: () => void;
  setConfirmedOrder: (value: React.SetStateAction<Order>) => void;
  confirmedOrder: Order;
  cartItemsConfirmed: OrderProductForDisplay[];
  timeRemaining?: number;
  cartExpire: Date;
  getMaxAvailable: (
    productUuid: string,
    eventRef?: string,
  ) => number | null;
  getCartQuantityCap: (
    productUuid: string,
    eventRef?: string,
  ) => number | null;
  getCartMinOrderQty: (
    productUuid: string,
    eventRef?: string,
  ) => number;
  isFirstOrderMinQtyEnforced: (eventHash: string) => boolean;
  registerEventSellOrderFlags: (
    events: Array<{ hash?: string | null; hasActiveSellOrders?: boolean }>
  ) => void;
  hasExceededMaxAvailable: () => boolean;
  hasBelowMinOrderQty: () => boolean;
  hasCartQuantityValidationErrors: () => boolean;
  setCheckoutErrorMessage: (value: string | undefined) => void;
};

const ShoppingCartContext = createContext({} as ShoppingCartContext);

export function useShoppingCart() {
  return useContext(ShoppingCartContext);
}
export function ShoppingCartProvider({ children }: ShoppingCartProviderProps) {
  const activePageId = useAppSelector((state) => state.builder.pages.active);
  const pages = useAppSelector((state) => state.builder.pages.list);
  const currentPageType =
    pages.find((page) => page.uuid === activePageId)?.pageType ?? null;

  const [isOpen, setIsOpen] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [deliveryMethods, setDeliveryMethods] = useState<DeliveryMethod[]>([]);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(null);

  const [showCartExpire, setShowCartExpire] = useState(false);
  const [showCheckoutErrorModal, setShowCheckoutErrorModal] = useState(false);
  const [checkoutErrorMessage, setCheckoutErrorMessage] = useState<
    string | undefined
  >(undefined);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [catalogProductsLoaded, setCatalogProductsLoaded] = useState(false);
  const [confirmedOrder, setConfirmedOrder] = useState<Order>(Object);
  const [cartItemsConfirmed, setCartItemsConfirmed] = useState<
    OrderProductForDisplay[]
  >([]);
  const [eventHasActiveSellOrders, setEventHasActiveSellOrders] = useState<
    Record<string, boolean>
  >({});
  const [totalCartSumConfirmed, setTotalCartSumConfirmed] = useState(0);
  const [totalCartDiscountConfirmed, setTotalCartDiscountConfirmed] =
    useState(0);
  const [deliveryConfirmed, setDeliveryConfirmed] =
    useState<CheckoutOrderDelivery | null>(null);
  // Cart expiry timeout (use ref to avoid async state races)
  const cartExpiryTimerRef = React.useRef<number | null>(null);

  const STORAGE_EXP_LENGTH = 10800000;
  // Fallback when salesPeriodEndBeforeEventDatetime is missing or invalid.
  const FALLBACK_MIN_ORDER_TIME_MS = 30 * 60 * 1000;
  const [cartExpire, setCartExpire] = useLocalStorage<Date>(
    "shopping-cart-expire",
    new Date(Date.now() + STORAGE_EXP_LENGTH)
  );
  const [cartItems, rawSetCartItems] = useLocalStorage<CartItem[]>(
    "shopping-cart",
    []
  );
  const cartItemsRef = React.useRef<CartItem[]>(cartItems);

  const sanitizeCartItems = React.useCallback((items: CartItem[]): CartItem[] => {
    const withUuid = items.filter(
      (item) =>
        typeof item.productUuid === "string" && item.productUuid.length > 0
    );
    return resyncAutoExtraCartLines(withUuid, productsRef.current);
  }, []);

  const setCartItems: typeof rawSetCartItems = React.useCallback(
    (nextValue) => {
      const next =
        typeof nextValue === "function"
          ? (nextValue as (current: CartItem[]) => CartItem[])(
              cartItemsRef.current
            )
          : nextValue;
      const sanitized = sanitizeCartItems(next);
      cartItemsRef.current = sanitized;
      rawSetCartItems(sanitized);
    },
    [rawSetCartItems, sanitizeCartItems]
  );

  useEffect(() => {
    const sanitized = sanitizeCartItems(cartItemsRef.current);
    if (!cartItemsEqualForSync(sanitized, cartItemsRef.current)) {
      cartItemsRef.current = sanitized;
      rawSetCartItems(sanitized);
    }
  }, [rawSetCartItems, sanitizeCartItems]);
  const [buyer, setBuyer] = useLocalStorage<Person>("buyer", Object);
  const [products, setProducts] = useLocalStorage<Product[]>("products", [], {
    hydrateFromStorage: false,
    persistInitialValue: false,
  });
  const productsRef = React.useRef<Product[]>(products);
  const latestProductsRequestRef = React.useRef(0);
  productsRef.current = products;
  cartItemsRef.current = cartItems;

  const catalogIndex = React.useMemo(
    () => buildCatalogIndex(products),
    [products]
  );

  const listableCatalogProducts = selectListableCatalogProducts(
    catalogIndex.parents
  );

  const getCatalogEntryByUuid = React.useCallback(
    (productUuid: string) => getCatalogEntry(catalogIndex, productUuid),
    [catalogIndex]
  );

  function cartMutationTarget(
    productUuid: string,
    eventRef?: string,
  ): CartLineMutationTarget {
    return { productUuid, eventRef };
  }

  function findCartLineIndex(
    currItems: CartItem[],
    target: CartLineMutationTarget
  ): number {
    return currItems.findIndex((item) =>
      cartItemMatchesManualMutationTarget(item, target)
    );
  }

  const getCatalogProductByUuid = React.useCallback(
    (productUuid: string) => getCatalogEntry(catalogIndex, productUuid)?.parent,
    [catalogIndex]
  );

  const [order, setOrder] = useLocalStorage<Order>(
    "order",
    ({ uuid: null, orderProducts: [] } as Order)
  );
  const collectEventHashesFromOrder = React.useCallback((orderData: Order): string[] => {
    const hashes = new Set<string>();
    for (const line of orderData.orderProducts ?? []) {
      const hash = line.eventHash?.trim();
      if (hash) {
        hashes.add(hash);
      }
    }
    return [...hashes];
  }, []);

  const syncEventSellOrderFlagsFromOrder = React.useCallback(
    (orderData: Order | null | undefined) => {
      if (!orderData?.uuid) {
        return;
      }

      const orderEventHashes = collectEventHashesFromOrder(orderData);
      const activeElsewhere = new Set(
        (orderData.eventHashesWithActiveSellOrders ?? [])
          .map((hash) => hash.trim())
          .filter((hash) => hash.length > 0)
      );

      if (orderEventHashes.length === 0 && activeElsewhere.size === 0) {
        return;
      }

      setEventHasActiveSellOrders((current) => {
        const next = { ...current };
        for (const hash of orderEventHashes) {
          next[hash] = activeElsewhere.has(hash);
        }
        for (const hash of activeElsewhere) {
          next[hash] = true;
        }
        return next;
      });

      setCartItems((current) =>
        current.map((item) => {
          const eventHash = (item.eventHash ?? "").trim();
          if (!eventHash) {
            return item;
          }
          if (!orderEventHashes.includes(eventHash) && !activeElsewhere.has(eventHash)) {
            return item;
          }
          const hasActive = activeElsewhere.has(eventHash);
          if (item.hasActiveSellOrders === hasActive) {
            return item;
          }
          return { ...item, hasActiveSellOrders: hasActive };
        })
      );
    },
    [collectEventHashesFromOrder, setCartItems]
  );

  const registerEventSellOrderFlags = React.useCallback(
    (
      events: Array<{ hash?: string | null; hasActiveSellOrders?: boolean }>
    ) => {
      if (events.length === 0) {
        return;
      }

      setEventHasActiveSellOrders((current) => {
        const next = { ...current };
        for (const event of events) {
          const hash = typeof event.hash === "string" ? event.hash.trim() : "";
          if (!hash || event.hasActiveSellOrders === undefined) {
            continue;
          }
          next[hash] = event.hasActiveSellOrders === true;
        }
        return next;
      });

      setCartItems((current) => applyEventSellOrderHintsToCart(current, events));
    },
    [setCartItems]
  );

  const isFirstOrderMinQtyEnforced = React.useCallback(
    (eventHash: string) => {
      return resolveFirstOrderMinQtyEnforced(
        eventHash,
        cartItemsRef.current,
        eventHasActiveSellOrders
      );
    },
    [eventHasActiveSellOrders]
  );

  const [additionalInfo, setAdditionalInfo] = useState("");
  const [pricingNow, setPricingNow] = useState(() => new Date());
  const pauseCatalogRefresh =
    !!confirmedOrder?.uuid &&
    (currentPageType === "order-complete" || currentPageType === "checkout");

  useEffect(() => {
    if (typeof order?.infoCustomer === "string") {
      setAdditionalInfo(order.infoCustomer);
    }
  }, [order?.uuid, order?.infoCustomer]);

  useEffect(() => {
    syncEventSellOrderFlagsFromOrder(order);
  }, [
    order?.uuid,
    order?.eventHashesWithActiveSellOrders,
    order?.orderProducts,
    syncEventSellOrderFlagsFromOrder,
  ]);

  useEffect(() => {
    if (!confirmedOrder?.uuid || confirmedOrder.orderCompleted !== true) {
      return;
    }
    if (paymentMethod?.uuid) {
      return;
    }
    const storedPaymentMethod = readConfirmedPaymentMethodFromStorage();
    if (storedPaymentMethod?.uuid) {
      setPaymentMethod(storedPaymentMethod);
    }
  }, [
    confirmedOrder?.uuid,
    confirmedOrder?.orderCompleted,
    paymentMethod?.uuid,
  ]);

  useEffect(() => {
    const tick = () => setPricingNow(new Date());
    const intervalId = window.setInterval(tick, 60_000);
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        tick();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  const [totalCartSum, setTotalCartSum] = useState(
    sumCartProductsTotal(cartItems, catalogIndex, new Date())
  );
  const [totalCartDiscount, setTotalCartDiscount] = useState(
    sumCartDiscount(cartItems, catalogIndex, new Date())
  );

  const loadProducts = React.useCallback(async () => {
    const requestId = latestProductsRequestRef.current + 1;
    latestProductsRequestRef.current = requestId;

    const noCacheHeaders = {
      "Content-Type": "application/json",
      "Cache-Control": "no-cache, no-store, must-revalidate",
      Pragma: "no-cache",
      Expires: "0",
    };

    let data: Product[];
    if (typeof API !== "undefined") {
      // Builder: reuse prefetch from App.js when available; otherwise fetch via axios.
      const prefetch = (window as BuilderCatalogProductsPrefetchWindow)
        .__catalogProductsPrefetch;
      if (prefetch) {
        delete (window as BuilderCatalogProductsPrefetchWindow)
          .__catalogProductsPrefetch;
        data = await prefetch;
      } else {
        // @ts-ignore
        const res = await window.axios("/products.json", {
          method: "GET",
          headers: noCacheHeaders,
          params: { _ts: Date.now() },
        });
        data = res.data;
      }
    } else {
      const response = await fetch(
        `/webstoreApi/products.json?_ts=${Date.now()}`,
        {
          method: "GET",
          cache: "no-store",
          headers: noCacheHeaders,
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      data = (await response.json()) as Product[];
    }

    if (requestId === latestProductsRequestRef.current) {
      const nextProducts = Array.isArray(data) ? [...data] : [];
      productsRef.current = nextProducts;
      setProducts((current) =>
        productsEqualForRefresh(current, nextProducts) ? current : nextProducts
      );
      setCartItems((curr) => curr);
    }
  }, [setCartItems, setProducts]);

  useEffect(() => {
    if (pauseCatalogRefresh) {
      return;
    }

    let isUnmounted = false;

    const refreshProducts = () => {
      void loadProducts().catch((error) => {
        console.error("Error loading products.json:", error);
      });
    };

    void loadProducts()
      .catch((error) => {
        console.error("Error loading products.json:", error);
      })
      .finally(() => {
        if (!isUnmounted) {
          setCatalogProductsLoaded(true);
          setProductsLoaded(true);
        }
      });

    const intervalId = window.setInterval(refreshProducts, 60_000);
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshProducts();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      isUnmounted = true;
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [loadProducts, pauseCatalogRefresh]);

  useEffect(() => {
    if (pauseCatalogRefresh) {
      return;
    }

    const eventName = "shopping-cart:route-change";
    let currentPathname = window.location.pathname;

    const onRouteChange = () => {
      const nextPathname = window.location.pathname;
      // Ignore query/hash-only transitions (e.g. category filter changes).
      if (nextPathname === currentPathname) {
        return;
      }
      currentPathname = nextPathname;
      void loadProducts().catch((error) => {
        console.error("Error loading products.json:", error);
      });
    };

    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    const patchedPushState: History["pushState"] = (...args) => {
      originalPushState.apply(window.history, args);
      window.dispatchEvent(new Event(eventName));
    };
    const patchedReplaceState: History["replaceState"] = (...args) => {
      originalReplaceState.apply(window.history, args);
      window.dispatchEvent(new Event(eventName));
    };

    window.history.pushState = patchedPushState;
    window.history.replaceState = patchedReplaceState;
    window.addEventListener("popstate", onRouteChange);
    window.addEventListener("hashchange", onRouteChange);
    window.addEventListener(eventName, onRouteChange);

    return () => {
      if (window.history.pushState === patchedPushState) {
        window.history.pushState = originalPushState;
      }
      if (window.history.replaceState === patchedReplaceState) {
        window.history.replaceState = originalReplaceState;
      }
      window.removeEventListener("popstate", onRouteChange);
      window.removeEventListener("hashchange", onRouteChange);
      window.removeEventListener(eventName, onRouteChange);
    };
  }, [loadProducts, pauseCatalogRefresh]);

  useEffect(() => {
    if (!catalogProductsLoaded) {
      return;
    }
    setCartItems((curr) => curr);
  }, [catalogProductsLoaded, products, setCartItems]);

  useEffect(() => {
    setTotalCartSum(sumCartProductsTotal(cartItems, catalogIndex, pricingNow));
    setTotalCartDiscount(
      sumCartDiscount(cartItems, catalogIndex, pricingNow)
    );
  }, [cartItems, catalogIndex, pricingNow]);

  // Set up cart expiry timer on mount and when order/cartExpire changes
  useEffect(() => {
    // Always clear existing timers first to prevent duplicates
    clearCartExpiryTimer();

    if (cartItems.length > 0) {
      // Ensure cartExpire is a Date object (in case useLocalStorage returns string)
      const currentExpiry =
        cartExpire instanceof Date ? cartExpire : new Date(cartExpire);
      const autoClean = order?.autoCleanExpiredCart === true;

      // Check if cart is already expired
      if (currentExpiry <= new Date()) {
        handleCartExpiry(autoClean);
      } else {
        setupCartExpiryTimer(currentExpiry, autoClean);
      }
    } else {
      // No cart items
    }

    // Cleanup timers when component unmounts
    return () => {
      clearCartExpiryTimer();
      clearCountdownTimer();
    };
  }, [cartExpire, order?.autoCleanExpiredCart, cartItems.length]);

  // Cart expiry is now handled by the reactive timer system
  // Legacy check removed to prevent conflicts

  function clearData() {
    clearCartExpiryTimer(); // Clear any active timers
    setCartExpireDate();
    setCartItems([]);
    setBuyer(Object);
    setOrder({ uuid: null, orderProducts: [] });
    localStorage.setItem("order", JSON.stringify({ uuid: null, orderProducts: [] }));
    setPaymentMethod(null);
    clearConfirmedPaymentMethodFromStorage();
    setDeliveryMethods([]);
    setPaymentMethods([]);
  }

  // Backend controls expiry only when there is an active order with a valid future expiry.
  // This avoids stale persisted order data blocking local expiry refresh on a fresh add-to-cart.
  function isBackendControlledExpiry(): boolean {
    if (!order?.uuid || !order?.shoppingCartExpire) {
      return false;
    }
    const backendExpiry = new Date(order.shoppingCartExpire);
    if (Number.isNaN(backendExpiry.getTime())) {
      return false;
    }
    return backendExpiry > new Date();
  }

  // Clear any existing expiry timeout
  function clearCartExpiryTimer() {
    if (cartExpiryTimerRef.current) {
      window.clearTimeout(cartExpiryTimerRef.current);
      cartExpiryTimerRef.current = null;
    }
  }

  // Countdown is handled locally in UI; no-op here to keep compatibility
  function clearCountdownTimer() {}


  // Set up reactive timer for cart expiry
  function setupCartExpiryTimer(expiryDate: Date, autoClean: boolean = false) {
    // Clear any existing timers to prevent duplicates
    clearCartExpiryTimer();
    clearCountdownTimer();

    const now = new Date();
    const expiry =
      expiryDate instanceof Date ? expiryDate : new Date(expiryDate);
    const timeUntilExpiry = expiry.getTime() - now.getTime();

    if (timeUntilExpiry <= 0) {
      // Already expired
      handleCartExpiry(autoClean);
      return;
    }

    const timer = window.setTimeout(() => {
      // Use a function to get current cart items instead of closure
      const currentCartItems = JSON.parse(
        localStorage.getItem("shopping-cart") || "[]"
      );

      if (currentCartItems.length === 0) {
        return;
      }

      if (autoClean) {
        clearData();
      } else {
        setShowCartExpire(true);
      }
    }, timeUntilExpiry);

    cartExpiryTimerRef.current = timer;

    // Visual countdown handled in UI component
  }

  // Handle cart expiry based on auto-clean setting
  function handleCartExpiry(autoClean: boolean = false) {
    if (cartItems.length === 0) {
      return;
    }

    if (autoClean) {
      clearData();
    } else {
      setShowCartExpire(true);
    }
  }

  function setCartExpireDate() {
    // Only extend locally if backend doesn't control expiry
    if (!isBackendControlledExpiry()) {
      const newExpiry = new Date(Date.now() + STORAGE_EXP_LENGTH);
      setCartExpire(newExpiry);

      // Clear existing timers before setting up new ones
      clearCartExpiryTimer();
      // Set up timer for local expiry (always shows modal)
      setupCartExpiryTimer(newExpiry, false);
    } else {
      // Backend controls expiry, not extending locally
    }
  }

  function parseOffsetMs(value: unknown): number | null {
    if (value === null || value === undefined || value === "") return null;

    if (typeof value === "number") {
      return Number.isFinite(value) && value >= 0 ? value : null;
    }

    const normalized = String(value).trim();
    if (normalized === "") return null;

    const numericMs = Number(normalized);
    return Number.isFinite(numericMs) && numericMs >= 0 ? numericMs : null;
  }

  function getTicketCutoffTimeMs(
    eventStart?: string,
    salesPeriodEndBeforeEventDatetime?: string | number | null
  ): number | null {
    const eventStartMs = new Date(eventStart || "").getTime();
    if (!Number.isFinite(eventStartMs)) return null;

    const minOrderMs =
      parseOffsetMs(salesPeriodEndBeforeEventDatetime) ??
      FALLBACK_MIN_ORDER_TIME_MS;

    return eventStartMs - minOrderMs;
  }

  /** Returns true if cart item has an event (eventHash). */
  function itemHasEvent(item: CartItem): boolean {
    return !!(item.eventHash);
  }


  /** Computes event-based expiry when cart has event tickets. Returns null if no event tickets or cutoff is in the past. */
  function computeEventBasedExpiry(): Date | null {
    const nowMs = Date.now();
    const defaultExpiryMs = nowMs + STORAGE_EXP_LENGTH;
    const ticketCutoffTimes: number[] = [];

    cartItems.forEach((item) => {
      if (!itemHasEvent(item) || !item.start) return;
      const cutoffMs = getTicketCutoffTimeMs(item.start, null);
      if (cutoffMs !== null) {
        ticketCutoffTimes.push(cutoffMs);
      }
    });

    if (ticketCutoffTimes.length === 0) return null;

    const earliestTicketCutoffMs = Math.min(...ticketCutoffTimes);
    const nextExpiryMs = Math.min(defaultExpiryMs, earliestTicketCutoffMs);

    if (nextExpiryMs <= nowMs) return null;
    return new Date(nextExpiryMs);
  }

  function setCartExpireDateForTicketAdd(
    eventStart?: string,
    salesPeriodEndBeforeEventDatetime?: string | number | null
  ) {
    if (isBackendControlledExpiry()) {
      return;
    }

    const nowMs = Date.now();
    const defaultExpiryMs = nowMs + STORAGE_EXP_LENGTH;
    const ticketCutoffTimes: number[] = [];

    const addedTicketCutoffMs = getTicketCutoffTimeMs(
      eventStart,
      salesPeriodEndBeforeEventDatetime
    );
    if (addedTicketCutoffMs !== null) {
      ticketCutoffTimes.push(addedTicketCutoffMs);
    }

    // Existing cart items do not store salesPeriodEndBeforeEventDatetime;
    // use fallback offset when computing mixed-cart earliest cutoff.
    cartItems.forEach((item) => {
      if (!itemHasEvent(item) || !item.start) return;
      const cutoffMs = getTicketCutoffTimeMs(item.start, null);
      if (cutoffMs !== null) {
        ticketCutoffTimes.push(cutoffMs);
      }
    });

    const earliestTicketCutoffMs =
      ticketCutoffTimes.length > 0 ? Math.min(...ticketCutoffTimes) : null;
    const nextExpiryMs =
      earliestTicketCutoffMs === null
        ? defaultExpiryMs
        : Math.min(defaultExpiryMs, earliestTicketCutoffMs);

    // If cutoff is already in the past, keep current expiry handling unchanged.
    if (nextExpiryMs <= nowMs) {
      return;
    }

    const newExpiry = new Date(nextExpiryMs);
    setCartExpire(newExpiry);
    clearCartExpiryTimer();
    setupCartExpiryTimer(newExpiry, false);
  }

  function setCartExpireDateFromBackend(
    backendExpireDate?: string,
    autoCleanExpiredCart?: boolean
  ) {
    // Clear any existing timers first to prevent duplicates
    clearCartExpiryTimer();

    // Handle all combinations of backend values
    if (backendExpireDate) {
      // Case 1: shoppingCartExpire is present
      const backendDate = new Date(backendExpireDate);

      if (!isNaN(backendDate.getTime())) {
        if (backendDate > new Date()) {
          // Valid future date - set backend expiry
          setCartExpire(backendDate);

          // Set up timer with auto-clean setting
          const shouldAutoClean = autoCleanExpiredCart === true;
          setupCartExpiryTimer(backendDate, shouldAutoClean);

          return true;
        } else {
          // Backend expiry is past
          if (autoCleanExpiredCart === true && cartItems.length > 0) {
            clearData();

            return true;
          }
          if (cartItems.length > 0) {
            setShowCartExpire(true);
          }

          return false;
        }
      } else {
        // Invalid backend date format – ignore and fallback below
      }
    } else if (autoCleanExpiredCart === true) {
      // Case 2: No shoppingCartExpire but autoCleanExpiredCart is true
      // Use event-aware expiry when cart has event tickets, else default
      const eventBasedExpiry = computeEventBasedExpiry();
      const localExpiry = eventBasedExpiry ?? new Date(Date.now() + STORAGE_EXP_LENGTH);
      setCartExpire(localExpiry);
      setupCartExpiryTimer(localExpiry, true);
      return true;
    }

    // Case 3: Neither present or invalid - fallback to local expiry.
    // Use event-aware expiry when cart has event tickets; otherwise preserve current if in future.
    const eventBasedExpiry = computeEventBasedExpiry();
    if (eventBasedExpiry) {
      setCartExpire(eventBasedExpiry);
      setupCartExpiryTimer(eventBasedExpiry, false);
      return false;
    }

    const currentExpiry =
      cartExpire instanceof Date ? cartExpire : new Date(cartExpire);
    const now = new Date();
    const fallbackExpiry =
      currentExpiry <= now
        ? new Date(Date.now() + STORAGE_EXP_LENGTH)
        : currentExpiry;

    if (fallbackExpiry !== currentExpiry) {
      setCartExpire(fallbackExpiry);
    }
    setupCartExpiryTimer(fallbackExpiry, false);
    return false;
  }

  function handleDataLayerCheckoutConfirm(orderNr: string) {
    // @ts-ignore
    window.dataLayer = window.dataLayer || [];
    const deliveryPrice = Number(order?.delivery?.taxInclusivePriceAmount ?? 0);

    const lineTotal = (cartItem: CartItem) => {
      if (!cartItem.productUuid) {
        return 0;
      }
      const entry = getCatalogEntry(catalogIndex, cartItem.productUuid);
      if (!entry) {
        return 0;
      }
      const { price } = getEffectivePrice(entry, pricingNow);
      return price * cartItem.quantity;
    };

    const productItems = cartItems.map((item) => {
      if (!item.productUuid) {
        return null;
      }
      const entry = getCatalogEntry(catalogIndex, item.productUuid);
      if (!entry) {
        return null;
      }
      const { price, priceRegular } = getEffectivePrice(entry, pricingNow);
      const parent = entry.parent;
      const productName = formatDataLayerProductName(
        parent.translations
      );
      const categories = formatDataLayerCategories(
        parent.productCategories ?? []
      );

      return {
        item_code: item.productUuid,
        item_name: productName,
        categories,
        price_regular: priceRegular,
        discount_amount: Number(priceRegular - price),
        quantity: item.quantity,
      };
    });

    // @ts-ignore
    window.dataLayer = window.dataLayer || [];
    // @ts-ignore
    window.dataLayer.push({
      event: "purchase",
      ecommerce: {
        total_amount: pf(
          cartItems.reduce((total, item) => total + lineTotal(item), 0) +
            (order?.delivery ? deliveryPrice : 0)
        ),
        total_amount_net: pf(
          cartItems.reduce((total, item) => total + lineTotal(item), 0) +
            (order?.delivery ? deliveryPrice : 0)
        ),
        products_amount: pf(
          cartItems.reduce((total, item) => total + lineTotal(item), 0)
        ),
        products_amount_net: pf(
          cartItems.reduce((total, item) => total + lineTotal(item), 0)
        ),
        shipping_amount: order?.delivery ? deliveryPrice : 0,
        shipping_amount_net: order?.delivery ? deliveryPrice : 0,

        order_id: orderNr,
        items: [...productItems],
      },
    });
  }

  function orderPay(onSuccess?: () => void) {
    if (!order?.uuid || !paymentMethod?.uuid) {
      setCheckoutErrorMessage("Order or payment method is missing.");
      setShowCheckoutErrorModal(true);
      return;
    }

    const payingWithPaymentMethod = paymentMethod;

    const payload = buildOrderPayRequest({
      orderUuid: order.uuid,
      paymentMethod: payingWithPaymentMethod,
      checkoutUrl: window.location.href,
    });

    // @ts-ignore
    window.axios
      .post("orderPay", payload)
      .then((res: any) => {
        const hostedPaymentRedirect =
          res?.data?.maksekeskusRedirect ?? res?.data?.montonioRedirect;

        if (hostedPaymentRedirect) {
          writeConfirmedPaymentMethodToStorage(payingWithPaymentMethod);
          redirectToHostedPayment(hostedPaymentRedirect);
        } else {
          const orderResponse = normalizeOrderApiResponse(res.data);
          if (hasOrderApiErrors(orderResponse)) {
            setCheckoutErrorMessage(extractOrderApiMessage(orderResponse));
            setShowCheckoutErrorModal(true);
            return;
          }
          handleDataLayerCheckoutConfirm(orderResponse.order?.orderNr ?? "");
          setConfirmedOrder(orderResponse.order ?? ({} as Order));
          setCartItemsConfirmed(
            enrichOrderProductsForConfirmedDisplay(
              orderResponse.order?.orderProducts ?? [],
              cartItems,
              productsRef.current
            )
          );
          const confirmedTotal = getConfirmedOrderGrandTotal(orderResponse.order);
          if (confirmedTotal != null) {
            setTotalCartSumConfirmed(confirmedTotal);
          }
          setTotalCartDiscountConfirmed(0);
          setDeliveryConfirmed(orderResponse.order?.delivery ?? null);

          if (payingWithPaymentMethod?.uuid) {
            setPaymentMethod(payingWithPaymentMethod);
            writeConfirmedPaymentMethodToStorage(payingWithPaymentMethod);
          }

          setCartItems([]);

          setOrder({ uuid: null, orderProducts: [] });

          // Find page with page type of "order_confirmed"
        }
        if (onSuccess) {
          onSuccess();
        }
      })
      .catch((error: any) => {
        console.log(error);
        const apiData = error?.response?.data;
        setCheckoutErrorMessage(extractOrderApiMessage(apiData));
        setShowCheckoutErrorModal(true);
      });
  }

  function handleCheckoutErrorModalClose() {
    setShowCheckoutErrorModal(false);
    setCheckoutErrorMessage(undefined);
    syncOrder();
  }

  function syncOrder(options?: {
    buyerData?: Person;
    delivery?: DeliveryFormData | { clear: true } | null;
    checkoutContext?: CheckoutContext;
    includeB2bContactPerson?: boolean;
  }): Promise<SyncOrderResult> {
    const currentCartItems = cartItemsRef.current;
    const sanitizedCartItems = sanitizeCartItems(currentCartItems);
    if (!cartItemsEqualForSync(sanitizedCartItems, currentCartItems)) {
      setCartItems(sanitizedCartItems);
    }
    const orderProducts = mapOrderProductsForRequest(sanitizedCartItems);
    const customer = mapBuyerToCustomer(
      (options?.buyerData ?? buyer) as unknown as BuyerFormData,
      { includeB2bContactPerson: options?.includeB2bContactPerson }
    );
    const delivery = mapDeliveryInputToPayload(options?.delivery);

    const postOrder = (
      orderUuid: string | null | undefined
    ): Promise<SyncOrderResult> => {
      const payload = buildSyncOrderRequest({
        orderUuid,
        orderProducts,
        customer,
        delivery,
        infoCustomer: additionalInfo || undefined,
        checkoutContext: options?.checkoutContext,
      });
      // @ts-ignore
      return window.axios
        .post("order", payload)
        .then((res: any) => {
          const data = normalizeOrderApiResponse(res.data);
          if (hasOrderApiErrors(data)) {
            if (hasOrderNotEditableError(data.errors) && orderUuid) {
              setOrder({ uuid: null, orderProducts: [] });
              return postOrder(null);
            }
            return {
              ok: false as const,
              message: extractOrderApiMessage(data),
              errors: data.errors,
            };
          }

        if (data.order) {
          setOrder(data.order);
          syncEventSellOrderFlagsFromOrder(data.order);
          if (typeof data.order.infoCustomer === "string") {
            setAdditionalInfo(data.order.infoCustomer);
          }
          setCartExpireDateFromBackend(
            data.order.shoppingCartExpire ?? undefined,
            data.order.autoCleanExpiredCart
          );

          const orderProductsSnapshot = data.order.orderProducts ?? [];
          setCartItems((curr) => {
            const withServerSnapshot = curr.map((item) => {
              const matching = findMatchingOrderProductForCartLine(
                item,
                orderProductsSnapshot
              );
              if (!matching) {
                return item;
              }
              const nextItem: CartItem = { ...item };
              if (
                !isAutoExtraCartLine(item) &&
                typeof matching.quantity === "number" &&
                Number.isFinite(matching.quantity)
              ) {
                nextItem.quantity = matching.quantity;
              }
              if (
                typeof matching.maxAvailable === "number" &&
                Number.isFinite(matching.maxAvailable)
              ) {
                nextItem.maxAvailable = matching.maxAvailable;
              }
              if (
                typeof matching.maxOrderQty === "number" &&
                Number.isFinite(matching.maxOrderQty) &&
                matching.maxOrderQty > 0
              ) {
                nextItem.maxOrderQty = matching.maxOrderQty;
              }
              if (
                typeof matching.minOrderQty === "number" &&
                Number.isFinite(matching.minOrderQty) &&
                matching.minOrderQty > 1
              ) {
                nextItem.minOrderQty = matching.minOrderQty;
              }
              if (matching?.eventStart) {
                nextItem.start = matching.eventStart;
              }
              if (
                typeof matching.eventHash === "string" &&
                matching.eventHash.trim().length > 0
              ) {
                nextItem.eventHash = matching.eventHash;
              }
              const matchingParentProductUuids = normalizeOrderParentProductUuids(
                matching as {
                  parentUuid?: string | null;
                  parentProductUuids?: string[];
                }
              );
              const backendMarksAsExtra =
                matching.cartLineSource === "autoExtra" ||
                (matchingParentProductUuids?.length ?? 0) > 0;

              if (backendMarksAsExtra) {
                nextItem.cartLineSource = "autoExtra";
              }
              if (matchingParentProductUuids?.length) {
                nextItem.parentProductUuids = matchingParentProductUuids;
              }

              if (matching.invalid === true) {
                nextItem.invalid = true;
              } else if (
                item.invalid === true &&
                isAutoExtraCartLine(item) &&
                !(matching.invalid === false && backendMarksAsExtra)
              ) {
                nextItem.invalid = true;
              } else {
                delete nextItem.invalid;
              }
              if (typeof matching.productName === "string" && matching.productName) {
                nextItem.productName = matching.productName;
              }
              return nextItem;
            });

            const retained = withServerSnapshot.filter((item) =>
              cartLineRetainedByOrderProducts(
                item,
                orderProductsSnapshot
              )
            );

            const withInvalidLines = mergeInvalidOrderProductsIntoCartItems(
              retained,
              orderProductsSnapshot
            );
            return withInvalidLines;
          });
        }

        if (data.paymentMethods) {
          setPaymentMethods(data.paymentMethods);
          if (typeof data.paymentMethod === "undefined") {
            setPaymentMethod((current) => {
              if (!current?.uuid) {
                return current;
              }
              return isMethodUuidInList(data.paymentMethods!, current.uuid)
                ? current
                : null;
            });
          }
        }

        let nextDeliveryMethods: DeliveryMethod[] | null = null;
        if (data.deliveryMethods) {
          const fixedUuid =
            options?.checkoutContext?.mode === "fixed"
              ? options.checkoutContext.deliveryMethodUuid
              : undefined;
          const methods =
            options?.checkoutContext?.mode === "fixed"
              ? fixedUuid === null
                ? []
                : data.deliveryMethods.filter((method) => method.uuid === fixedUuid)
              : data.deliveryMethods;
          nextDeliveryMethods = methods;
          setDeliveryMethods(methods);
        }
        const orderCompleted = data.order?.orderCompleted === true;

        if (typeof data.paymentMethod !== "undefined") {
          if (data.paymentMethod?.uuid) {
            setPaymentMethod(data.paymentMethod);
            writeConfirmedPaymentMethodToStorage(data.paymentMethod);
          } else if (!orderCompleted) {
            setPaymentMethod(data.paymentMethod ?? null);
          }
        }
        if (data.order?.delivery) {
          setDeliveryConfirmed(data.order.delivery);
        }

        if (orderCompleted && data.order) {
          setConfirmedOrder(data.order);
          setCartItemsConfirmed(
            enrichOrderProductsForConfirmedDisplay(
              data.order.orderProducts ?? [],
              cartItems,
              productsRef.current
            )
          );
          const restoredTotal = getConfirmedOrderGrandTotal(data.order);
          if (restoredTotal != null) {
            setTotalCartSumConfirmed(restoredTotal);
          }
          setTotalCartDiscountConfirmed(0);
          setDeliveryConfirmed(data.order.delivery ?? null);

          const storedPaymentMethod = readConfirmedPaymentMethodFromStorage();
          if (storedPaymentMethod?.uuid) {
            setPaymentMethod(storedPaymentMethod);
          }

          setCartItems([]);
          setOrder({ uuid: null, orderProducts: [] });
        }

        if (!data.order) {
          console.log(
            "No order returned from backend, using local cart expiry"
          );
          setCartExpireDate();
        }

          const hasStaleDelivery =
            !orderCompleted &&
            !!data.order?.uuid &&
            !!data.order?.delivery?.uuid &&
            nextDeliveryMethods !== null &&
            !isMethodUuidInList(nextDeliveryMethods, data.order.delivery.uuid) &&
            typeof options?.delivery === "undefined";

          if (hasStaleDelivery) {
            return syncOrder({
              delivery: null,
              checkoutContext: options?.checkoutContext,
              buyerData: options?.buyerData,
              includeB2bContactPerson: options?.includeB2bContactPerson,
            });
          }

          return { ok: true as const, data };
        })
        .catch((error: any) => {
          const apiData = error?.response?.data;
          const errors = formatOrderApiErrors(apiData);
          if (hasOrderNotEditableError(errors) && orderUuid) {
            setOrder({ uuid: null, orderProducts: [] });
            return postOrder(null);
          }
          console.log(error);
          // On error, fallback to local expiry to ensure cart doesn't stay expired indefinitely
          setCartExpireDate();
          return {
            ok: false as const,
            message: extractOrderApiMessage(apiData),
            errors: errors.length ? errors : undefined,
          };
        });
    };

    return postOrder(order?.uuid);
  }

  const cartQuantity = cartItems.reduce(
    (quantity, item) => item.quantity + quantity,
    0
  );

  const openCart = () => setIsOpen(true);
  const closeCart = () => setIsOpen(false);
  function getItemQuantity(productUuid: string) {
    return cartItems.find((item) => item.productUuid === productUuid)?.quantity || 0;
  }

  function handleDataLayerAddToCart(productUuid: string) {
    const entry = getCatalogEntry(catalogIndex, productUuid);
    if (!entry) {
      return;
    }
    const { price, priceRegular } = getEffectivePrice(entry, pricingNow);
    const parent = entry.parent;
    const productName = formatDataLayerProductName(parent.translations);
    const categories = formatDataLayerCategories(parent.productCategories ?? []);

    // @ts-ignore
    window.dataLayer = window.dataLayer || [];
    // @ts-ignore
    window.dataLayer.push({
      event: "add_to_cart",
      ecommerce: {
        products_amount: Number(price),
        products_amount_net: Number(price),
        items: [
          {
            item_code: productUuid,
            item_name: productName,
            categories,
            price_regular: priceRegular,
            discount_amount: Number(priceRegular - price),
            quantity: 1,
          },
        ],
      },
    });
  }

  function applyCartWithExtraResync(
    updater: (curr: CartItem[]) => CartItem[]
  ): void {
    setCartItems((curr) => updater(curr));
  }

  function toggleCart() {
    setIsOpen((curr) => !curr);
  }
  function increaseCartQuantity(
    productUuid: string,
    eventRef?: string,
    start?: string,
    skipOpenCart?: boolean,
    salesPeriodEndBeforeEventDatetime?: string | number | null,
    maxAvailable?: number,
    maxOrderQty?: number,
    minOrderQty?: number,
    minQty?: number,
    hasActiveSellOrders?: boolean,
    cartLineSource?: CartItem["cartLineSource"],
  ) {
    // For ticket add-to-cart, apply ticket-aware expiry calculation.
    if (eventRef && start) {
      setCartExpireDateForTicketAdd(start, salesPeriodEndBeforeEventDatetime);
    } else {
      // Keep existing behavior for non-ticket scenarios.
      setCartExpireDate();
    }
    const target = cartMutationTarget(productUuid, eventRef);
    const resolvedmaxOrderQty =
      typeof maxOrderQty === "number" && maxOrderQty > 0 ? maxOrderQty : undefined;
    const resolvedMinOrderQty = parseMinOrderQtyForCart(minOrderQty);
    const resolvedMinQty = parseMinQtyForCart(minQty);
    const resolvedHasActiveSellOrders =
      typeof hasActiveSellOrders === "boolean" ? hasActiveSellOrders : undefined;

    applyCartWithExtraResync((currItems) => {
      const existingItemIndex = findCartLineIndex(currItems, target);

      // If item does not exist, add it with quantity 1
      if (existingItemIndex === -1) {
        return [
          ...currItems,
          {
            productUuid,
            quantity: 1,
            maxAvailable,
            maxOrderQty: resolvedmaxOrderQty,
            minQty: resolvedMinQty,
            minOrderQty: resolvedMinOrderQty,
            eventHash: eventRef,
            start,
            ...(resolvedHasActiveSellOrders !== undefined
              ? { hasActiveSellOrders: resolvedHasActiveSellOrders }
              : {}),
            ...(cartLineSource ? { cartLineSource } : {}),
          },
        ];
      }

      const existingItem = currItems[existingItemIndex];
      const nextItem: CartItem = {
        ...existingItem,
        productUuid,
        quantity: existingItem.quantity + 1,
        maxAvailable:
          typeof maxAvailable === "number"
            ? maxAvailable
            : existingItem.maxAvailable,
        maxOrderQty: resolvedmaxOrderQty ?? existingItem.maxOrderQty,
        minQty: resolvedMinQty ?? existingItem.minQty,
        minOrderQty: resolvedMinOrderQty ?? existingItem.minOrderQty,
        eventHash: eventRef,
        start,
        ...(resolvedHasActiveSellOrders !== undefined
          ? { hasActiveSellOrders: resolvedHasActiveSellOrders }
          : {}),
        ...(cartLineSource ? { cartLineSource } : {}),
      };
      const cap = eventRef
        ? resolveEventCartLineQuantityCap(
            currItems,
            order?.orderProducts,
            productUuid,
            eventRef
          ) ?? getCartLineQuantityCap(nextItem)
        : getCartLineQuantityCap(nextItem);
      if (cap !== undefined && nextItem.quantity > cap) {
        return currItems;
      }

      // Otherwise, increment the quantity
      return currItems.map((item, idx) =>
        idx === existingItemIndex ? nextItem : item
      );
    });

    if (!skipOpenCart) openCart();

    if (productUuid) handleDataLayerAddToCart(productUuid);
  }
  function decreaseCartQuantity(
    productUuid: string,
    eventRef?: string,
  ) {
    // Only extend expiry if backend doesn't control it
    setCartExpireDate();
    const target = cartMutationTarget(productUuid, eventRef);
    applyCartWithExtraResync((currItems) => {
      const existingItemIndex = findCartLineIndex(currItems, target);

      if (existingItemIndex === -1) return currItems;

      const existingItem = currItems[existingItemIndex];

      const minQty = eventRef
        ? resolveEventCartLineMinOrderQty(
            currItems,
            order?.orderProducts,
            productUuid,
            eventRef,
            eventHasActiveSellOrders
          ) ??
          resolveEffectiveMinQty(
            existingItem.minQty,
            existingItem.minOrderQty,
            isFirstOrderMinQtyEnforced(eventRef)
          )
        : resolveMinOrderQty(existingItem.minOrderQty);

      if (eventRef) {
        const cap =
          resolveEventCartLineQuantityCap(
            currItems,
            order?.orderProducts,
            productUuid,
            eventRef
          ) ?? getCartLineQuantityCap(existingItem);
        if (cap !== undefined && existingItem.quantity > cap) {
          return currItems.map((item, index) => {
            if (index === existingItemIndex) {
              return {
                ...item,
                quantity: clampQuantityToMinAndCap(
                  existingItem.quantity,
                  existingItem.minOrderQty,
                  cap
                ),
              };
            }
            return item;
          });
        }
      }

      if (existingItem.quantity <= minQty) {
        return currItems.filter(
          (item) => !cartItemMatchesManualMutationTarget(item, target)
        );
      }

      return currItems.map((item, index) => {
        if (index === existingItemIndex) {
          return { ...item, quantity: item.quantity - 1 };
        }
        return item;
      });
    });
  }
  function setCartQuantity(
    quantity: number,
    productUuid: string,
    eventRef?: string,
    _start?: string,
  ) {
    // Only extend expiry if backend doesn't control it
    setCartExpireDate();
    const target = cartMutationTarget(productUuid, eventRef);
    applyCartWithExtraResync((currItems) => {
      if (quantity <= 0) {
        return currItems.filter(
          (item) => !cartItemMatchesManualMutationTarget(item, target)
        );
      }

      const existingItemIndex = findCartLineIndex(currItems, target);
      const existingItem =
        existingItemIndex >= 0 ? currItems[existingItemIndex] : undefined;

      let clampedQuantity = quantity;
      if (eventRef) {
        clampedQuantity = clampEventCartLineQuantity(
          quantity,
          currItems,
          order?.orderProducts,
          productUuid,
          eventRef,
          eventHasActiveSellOrders,
          existingItem
        );
      } else if (existingItem) {
        const cap = getCartLineQuantityCap(existingItem);
        clampedQuantity = clampQuantityToMinAndCap(
          quantity,
          existingItem.minOrderQty,
          cap
        );
      } else {
        clampedQuantity = clampQuantityToMinOrder(quantity, undefined);
      }

      if (clampedQuantity <= 0) {
        return currItems.filter(
          (item) => !cartItemMatchesManualMutationTarget(item, target)
        );
      }

      if (existingItemIndex === -1) {
        return [
          ...currItems,
          {
            productUuid,
            quantity: clampedQuantity,
            eventHash: eventRef,
          },
        ];
      }

      return currItems.map((item, index) => {
        if (index === existingItemIndex) {
          return { ...item, quantity: clampedQuantity };
        }
        return item;
      });
    });
  }
  function removeFromCart(
    productUuid: string,
    eventRef?: string,
  ) {
    // Only extend expiry if backend doesn't control it
    setCartExpireDate();
    const target = cartMutationTarget(productUuid, eventRef);
    applyCartWithExtraResync((currItems) => {
      return currItems.filter(
        (item) => !cartItemMatchesManualMutationTarget(item, target)
      );
    });
  }

  function getCartQuantityCap(
    productUuid: string,
    eventRef?: string
  ): number | null {
    if (!eventRef) {
      return null;
    }
    const cap = resolveEventCartLineQuantityCap(
      cartItems,
      order?.orderProducts,
      productUuid,
      eventRef
    );
    return cap ?? null;
  }

  // Effective selectable qty (stock capped by maxOrderQty) for event cart lines.
  function getMaxAvailable(
    productUuid: string,
    eventRef?: string | number,
  ): number | null {
    if (typeof eventRef !== "string") {
      return null;
    }
    return getCartQuantityCap(productUuid, eventRef);
  }

  // Check if any cart items exceed their maxAvailable (for events)
  function hasExceededMaxAvailable(): boolean {
    return cartItems.some((cartItem) => {
      // Auto extras scale with parent qty; ticket maxAvailable does not apply to their line qty.
      if (isAutoExtraCartLine(cartItem)) {
        return false;
      }

      if (!itemHasEvent(cartItem)) return false;

      const cap =
        resolveEventCartLineQuantityCap(
          cartItems,
          order?.orderProducts,
          cartItem.productUuid,
          cartItem.eventHash ?? ""
        ) ?? getCartLineQuantityCap(cartItem);
      return cap !== undefined && cartItem.quantity > cap;
    });
  }

  function getCartMinOrderQty(
    productUuid: string,
    eventRef?: string
  ): number {
    if (!eventRef) {
      return 1;
    }
    const minOrderQty = resolveEventCartLineMinOrderQty(
      cartItems,
      order?.orderProducts,
      productUuid,
      eventRef,
      eventHasActiveSellOrders
    );
    const fromCart = cartItems.find(
      (item) =>
        item.productUuid === productUuid && item.eventHash === eventRef
    );
    return (
      minOrderQty ??
      resolveEffectiveMinQty(
        fromCart?.minQty,
        fromCart?.minOrderQty,
        isFirstOrderMinQtyEnforced(eventRef)
      )
    );
  }

  function hasBelowMinOrderQty(): boolean {
    return cartItems.some((cartItem) => {
      if (isAutoExtraCartLine(cartItem) || !itemHasEvent(cartItem)) {
        return false;
      }

      const eventRef = cartItem.eventHash ?? "";
      const enforceFirstOrderMin = isFirstOrderMinQtyEnforced(eventRef);

      return isBelowEffectiveMinQty(
        cartItem.quantity,
        cartItem.minQty,
        cartItem.minOrderQty,
        enforceFirstOrderMin
      );
    });
  }

  function hasCartQuantityValidationErrors(): boolean {
    return hasExceededMaxAvailable() || hasBelowMinOrderQty();
  }

  useEffect(() => {
    if (cartItems.length === 0) return;

    for (const item of cartItems) {
      if (isAutoExtraCartLine(item) || !itemHasEvent(item) || !item.eventHash) {
        continue;
      }
      if (item.quantity <= 0) continue;

      const eventRef = item.eventHash;
      const cap =
        resolveEventCartLineQuantityCap(
          cartItems,
          order?.orderProducts,
          item.productUuid,
          eventRef
        ) ?? undefined;
      const minOrderQty = resolveEventCartLineMinOrderQty(
        cartItems,
        order?.orderProducts,
        item.productUuid,
        eventRef,
        eventHasActiveSellOrders
      );

      const fixedQty =
        cap !== undefined
          ? clampQuantityToMinAndCap(item.quantity, minOrderQty, cap)
          : clampQuantityToMinOrder(item.quantity, minOrderQty);

      if (fixedQty <= 0) {
        removeFromCart(item.productUuid, eventRef);
        return;
      }
      if (fixedQty !== item.quantity) {
        setCartQuantity(fixedQty, item.productUuid, eventRef);
        return;
      }
    }
  }, [cartItems, order?.orderProducts, eventHasActiveSellOrders]);

  return (
    <ShoppingCartContext.Provider
      value={{
        setShowCartExpire,
        productsLoaded,
        catalogProductsLoaded,
        listableCatalogProducts,
        catalogIndex,
        getCatalogEntryByUuid,
        getCatalogProductByUuid,
        setCartExpire,
        getItemQuantity,
        showCartExpire,
        showCheckoutErrorModal,
        checkoutErrorMessage,
        setShowCheckoutErrorModal,
        handleCheckoutErrorModalClose,
        setCartExpireDate,
        increaseCartQuantity,
        decreaseCartQuantity,
        setCartQuantity,
        removeFromCart,
        openCart,
        closeCart,
        toggleCart,
        cartItems,
        STORAGE_EXP_LENGTH,
        cartQuantity,
        additionalInfo,
        setAdditionalInfo,
        totalCartSum,
        totalCartDiscount,
        setTotalCartDiscount,
        deliveryConfirmed,
        totalCartDiscountConfirmed,
        totalCartSumConfirmed,
        products,
        setProducts,
        pricingNow,
        clearData,
        buyer,
        setBuyer,
        order,
        syncOrder,
        getMaxAvailable,
        getCartQuantityCap,
        getCartMinOrderQty,
        isFirstOrderMinQtyEnforced,
        registerEventSellOrderFlags,
        hasExceededMaxAvailable,
        hasBelowMinOrderQty,
        hasCartQuantityValidationErrors,
        setCheckoutErrorMessage,
        deliveryMethods,
        paymentMethods,
        paymentMethod,
        setPaymentMethod,
        orderPay,
        setConfirmedOrder,
        confirmedOrder,
        cartItemsConfirmed,
        timeRemaining: 0,
        cartExpire,
      }}
    >
      {children}
      <ShoppingCart isOpen={isOpen} />
    </ShoppingCartContext.Provider>
  );
}
